/**
 * Remove Markdown syntax from text
 */
export function removeMarkdown(text: string): string {
  let result = text;
  
  // Remove bold: **text** or __text__
  result = result.replace(/\*\*(.+?)\*\*/g, '$1');
  result = result.replace(/__(.+?)__/g, '$1');
  
  // Remove italic: *text* or _text_
  result = result.replace(/\*(.+?)\*/g, '$1');
  result = result.replace(/_(.+?)_/g, '$1');
  
  // Convert list markers to bullets
  result = result.replace(/^\s*[\*\-]\s+/gm, '• ');
  
  // Remove headers: # text
  result = result.replace(/^#+\s+/gm, '');
  
  // Remove inline code: `text`
  result = result.replace(/`(.+?)`/g, '$1');
  
  return result;
}

/**
 * Normalize and validate Brazilian phone numbers for WhatsApp
 */
export function normalizePhoneNumber(phone: string): { 
  isValid: boolean; 
  whatsappUrl?: string;
  isMobile?: boolean;
} {
  // Remove all non-numeric characters
  const digits = phone.replace(/\D/g, '');
  
  // Validate Brazilian mobile (9 after area code)
  // Valid formats: 11 digits (DDDxxxxxxxxx) or 13 digits (55DDDxxxxxxxxx)
  
  if (digits.length === 11 && digits[2] === '9') {
    // Format: DDDxxxxxxxxx
    return {
      isValid: true,
      isMobile: true,
      whatsappUrl: `https://wa.me/55${digits}`
    };
  }
  
  if (digits.length === 13 && digits.startsWith('55') && digits[4] === '9') {
    // Format: 55DDDxxxxxxxxx
    return {
      isValid: true,
      isMobile: true,
      whatsappUrl: `https://wa.me/${digits}`
    };
  }
  
  // Valid landline but not mobile
  if (digits.length === 10 || digits.length === 12) {
    return { isValid: true, isMobile: false };
  }
  
  return { isValid: false };
}

/**
 * Detect and extract URLs, emails, and phones from text
 */
export function extractLinks(text: string): Array<{ 
  type: 'text' | 'url' | 'email' | 'phone'; 
  content: string; 
  start: number; 
  end: number;
  whatsappUrl?: string;
  isMobile?: boolean;
}> {
  const parts: Array<{ 
    type: 'text' | 'url' | 'email' | 'phone'; 
    content: string; 
    start: number; 
    end: number;
    whatsappUrl?: string;
    isMobile?: boolean;
  }> = [];
  
  // Regex patterns
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  const phoneRegex = /(?:\+?55\s?)?(?:\(?\d{2}\)?\s?)?(?:9\s?)?\d{4,5}[-\s]?\d{4}/g;
  
  let lastIndex = 0;
  const matches: Array<{ 
    type: 'url' | 'email' | 'phone'; 
    content: string; 
    index: number;
    whatsappUrl?: string;
    isMobile?: boolean;
  }> = [];
  
  // Find all URLs
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    matches.push({
      type: 'url',
      content: match[0],
      index: match.index
    });
  }
  
  // Find all emails
  while ((match = emailRegex.exec(text)) !== null) {
    matches.push({
      type: 'email',
      content: match[0],
      index: match.index
    });
  }
  
  // Find all phones
  while ((match = phoneRegex.exec(text)) !== null) {
    const phoneInfo = normalizePhoneNumber(match[0]);
    if (phoneInfo.isValid) {
      matches.push({
        type: 'phone',
        content: match[0],
        index: match.index,
        whatsappUrl: phoneInfo.whatsappUrl,
        isMobile: phoneInfo.isMobile
      });
    }
  }
  
  // Sort matches by position
  matches.sort((a, b) => a.index - b.index);
  
  // Build parts array
  matches.forEach(match => {
    // Add text before this match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index),
        start: lastIndex,
        end: match.index
      });
    }
    
    // Add the match
    parts.push({
      type: match.type,
      content: match.content,
      start: match.index,
      end: match.index + match.content.length,
      whatsappUrl: match.whatsappUrl,
      isMobile: match.isMobile
    });
    
    lastIndex = match.index + match.content.length;
  });
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex),
      start: lastIndex,
      end: text.length
    });
  }
  
  // If no matches found, return the whole text
  if (parts.length === 0) {
    parts.push({
      type: 'text',
      content: text,
      start: 0,
      end: text.length
    });
  }
  
  return parts;
}
